import { z } from '@customerio/hydra-validation';
import type { Customer as ApiCustomer, CustomersDeleteParams, operations } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * Customer (profile) entity — workspace-scoped.
 *
 * The onboarding/test-recipient flows only need a few aggregate reads
 * (test users, test-user count, a people list page) plus the create +
 * double-opt-in actions, so this is a minimal entity: the read schema is a
 * passthrough (the wire `id` is already a string) and the extra cache slots
 * for the aggregate reads live on `customerKeys` below.
 */
/**
 * A device on a customer profile, derived from the generated `Customer.devices`
 * element type (typed in the Services spec via customerio/services#23464).
 * Devices arrive embedded in the customer GET payload (there is no separate
 * devices endpoint). `attributes` holds SDK-reported values (`app_version`,
 * `device_os`, `push_enabled`, …) as strings; `last_used` is epoch seconds.
 */
export type CustomerDevice = NonNullable<ApiCustomer['devices']>[number];

/**
 * Customer (profile) entity — workspace-scoped. Kept identical to the generated
 * `ApiCustomer` so it stays compatible with every source that produces a
 * customer (People list, activity logs, …). Read a typed device list via the
 * `customerDevices` accessor (`utils.ts`) rather than reaching into
 * `customer.devices` at each call site.
 */
export type Customer = ApiCustomer;

const CustomerFromApi = readSchema<Customer>(z.looseObject({}));

/** @public */
export const customer = defineEntity<Customer>({
  name: 'customer',
  scope: 'workspace',
  pk: (record) => record.id,
  schema: { read: CustomerFromApi },
});

/**
 * The People list query params, derived from the customers list endpoint so
 * they can't drift from the spec: `sort` / `asc` / `page` / `email` / `filters`
 * (`email` and `filters` are mutually exclusive, per the API description).
 */
export type PeopleListParams = NonNullable<
  operations['get-v1-environments-environment_id-customers']['parameters']['query']
>;

/**
 * A search for people by one lookup: an email address, or one encoded
 * condition. The customers endpoint treats the two as exclusive, so a search
 * is always exactly one of them.
 */
export type PeopleLookup = { email: string } | { filters: string };

/**
 * Pagination params (`page` / `size`) for the single-type related-objects read
 * (the per-type tables on the Relationships tab), derived from the endpoint so
 * they can't drift from the spec.
 */
export type RelatedObjectsPageParams = NonNullable<
  operations['get-v1-environments-environment_id-object_types-type_id-relationships-objects-related_to-cio_id']['parameters']['query']
>;

/** The log filters a profile panel reads its own activity with, derived from the logs endpoint. */
export type CustomerLogsParams = Pick<
  NonNullable<operations['get-v1-environments-environment_id-logs']['parameters']['query']>,
  'type' | 'limit'
>;

/**
 * The people list `meta.pagination.total` is `-1` (not a count) when an email
 * search matches more than ~2000 profiles — the backend reports the total as
 * unknown. Callers must branch on this rather than treating it as a number.
 */
export function isUnknownPeopleTotal(total: number): boolean {
  return total < 0;
}

export const customerKeys = {
  ...customer.keys,
  /** Test-user list slot; `verified` selects the confirmed-only variant. */
  testUsers: (workspaceId: number, verified?: boolean) =>
    [...customer.keys.all(workspaceId), 'test-users', verified === true ? 'verified' : 'all'] as const,
  /** Test-user count + plan limit. */
  testUserCount: (workspaceId: number) => [...customer.keys.all(workspaceId), 'test-user-count'] as const,
  /** Paginated people list. */
  people: (workspaceId: number, params: PeopleListParams) =>
    [...customer.keys.all(workspaceId), 'people', params] as const,
  /** Unfiltered people count — drives the welcome/empty state. */
  unfilteredCount: (workspaceId: number) => [...customer.keys.all(workspaceId), 'unfiltered-count'] as const,
  /** The first page of people matching a base64-encoded condition filter, with their count. */
  filteredPeople: (workspaceId: number, filters: string) =>
    [...customer.keys.all(workspaceId), 'filtered-people', filters] as const,
  /** How many people an optional base64-encoded condition filter matches, and the index's notice. */
  audienceMatch: (workspaceId: number, filters: string | undefined) =>
    [...customer.keys.all(workspaceId), 'audience-match', filters ?? null] as const,
  /** Count of people matching a base64-encoded condition filter. */
  filteredCount: (workspaceId: number, filters: string) =>
    [...customer.keys.all(workspaceId), 'filtered-count', filters] as const,
  /** The merge preview for a primary/secondary pair (internal ids). */
  mergePreview: (workspaceId: number, primaryCustomerId: string, secondaryCustomerId: string) =>
    [...customer.keys.all(workspaceId), 'merge-preview', primaryCustomerId, secondaryCustomerId] as const,
  /** How many of a filter-based bulk delete's matches were recently active, keyed by the delete's filters. */
  deleteActivityCheck: (workspaceId: number, filters: CustomersDeleteParams['filters']) =>
    [...customer.keys.all(workspaceId), 'delete-activity-check', filters ?? null] as const,
  /** A profile's standing consent records, keyed by its cio id. */
  consentState: (workspaceId: number, cioId: string) =>
    [...customer.keys.all(workspaceId), 'consent-state', cioId] as const,
  /** A profile's opt-outs (per-channel unsubscribe state). */
  optOuts: (workspaceId: number, customerId: string) =>
    [...customer.keys.all(workspaceId), 'opt-outs', customerId] as const,
  /** The ids of the opt-outs queued for removal, which the opt-outs read still lists until the backend catches up. */
  optOutsQueued: (workspaceId: number, customerId: string) =>
    [...customer.keys.all(workspaceId), 'opt-outs-queued', customerId] as const,
  /** Every profile's rendered profile shortcuts: what a change to a shortcut invalidates. */
  profileShortcutsAll: (workspaceId: number) => [...customer.keys.all(workspaceId), 'profile-shortcuts'] as const,
  /** A profile's rendered profile shortcuts. */
  profileShortcuts: (workspaceId: number, customerId: string) =>
    [...customerKeys.profileShortcutsAll(workspaceId), customerId] as const,
  /** A profile's activity logs, keyed by its internal id (`cio_id`) and the log filter. */
  logs: (workspaceId: number, cioId: string, params: CustomerLogsParams) =>
    [...customer.keys.all(workspaceId), 'logs', cioId, params] as const,
  /** A profile's related objects of every type, at most ten each (a composer's sample data). */
  relatedObjectsAllTypes: (workspaceId: number, cioId: string) =>
    [...customer.keys.all(workspaceId), 'related-objects', 'all-types', cioId] as const,
  /** Every per-type related-objects read for a profile: what a write to its relationships invalidates. */
  relatedObjectsForTypeAll: (workspaceId: number, cioId: string) =>
    [...customer.keys.all(workspaceId), 'related-objects', 'for-type', cioId] as const,
  /**
   * Every page of one type's table. A delete in that table refetches these and leaves the
   * Relationships tab's type probes alone, so the emptied table stays on screen as it does in Ember.
   */
  relatedObjectsForTypePages: (workspaceId: number, cioId: string, typeId: string) =>
    [...customerKeys.relatedObjectsForTypeAll(workspaceId, cioId), 'pages', typeId] as const,
  /** A profile's related objects of a single type, paginated (Relationships tab). */
  relatedObjectsForType: (workspaceId: number, cioId: string, typeId: string, params: RelatedObjectsPageParams) =>
    [...customerKeys.relatedObjectsForTypePages(workspaceId, cioId, typeId), params] as const,
  /** The single-row read that tells the Relationships tab whether a type has any relationships. */
  relatedObjectTypeProbe: (workspaceId: number, cioId: string, typeId: string) =>
    [...customerKeys.relatedObjectsForTypeAll(workspaceId, cioId), 'probe', typeId] as const,
};
